/**
 * ZustandGantt - Gantt chart using the Zustand store implementation.
 *
 * Route: /schedule/tasks
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactGantt, { ReactGanttRef } from '@dhx/react-gantt';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  useMantineTheme,
  useComputedColorScheme,
  Modal,
  Text,
  Group,
  Button,
  Tooltip,
  Anchor,
  Stack,
  SimpleGrid,
  Box,
  Badge,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconRefresh,
  IconDeviceFloppy,
  IconTargetArrow,
  IconInfoCircle,
  IconCalendarEvent,
  IconMapPin,
  IconTags,
  IconFileTypeXml,
  IconFileSpreadsheet,
  IconFileTypePdf,
  IconUserPlus,
  IconLayoutSidebar,
} from '@tabler/icons-react';
import { GanttTaskSearchField } from './components/GanttTaskSearchField';
import { GanttKeyboardShortcutsModal } from './components/GanttKeyboardShortcutsModal';
import { GanttLiveBadge } from './components/GanttLiveBadge';
import { GanttColumnsMenu } from './components/GanttColumnsMenu';
import { GanttTasksMenu } from './components/GanttTasksMenu';
import { GanttAnalysisMenu } from './components/GanttAnalysisMenu';
import { GanttHelpMenu } from './components/GanttHelpMenu';
import { GanttExportMenu } from './components/GanttExportMenu';
import { GanttExpandCollapseButton } from './components/GanttExpandCollapseButton';
import type { GanttToolsActionItem } from './components/tools/GanttToolsActionList';
import {
  collectTaskNameSearchVisibleTaskIds,
  normalizeTaskSearchQuery,
} from '../../gantt/taskNameSearch';
import {
  TaskStoreProvider,
  useTaskStore,
  useDhtmlxTasks,
  useLinksRevision,
  useLocationTree,
  useSelectedTaskId,
  useTaskStoreLoading,
  useSessionMetadata,
  usePendingChangeTaskIds,
  usePendingEditedFields,
} from '../../hooks/useTaskStore';
import { getProjectId } from '../../utils/cookies';
import { getProjectDisplayName } from '../../utils/projectDisplay';
import {
  PERMISSION_RESTRICTED_MESSAGE,
  PermissionRestrictedTooltip,
  useCanSchedulerAction,
} from '../../permissions/scheduler';
import {
  formatDateForBackend,
  dependencyTypeToDhtmlx,
  dependencyTypeFromDhtmlx,
  transformTaskSnapshotToPayload,
  parseDateSafe,
  mapPlanningApiTaskRowToSnapshot,
  unwrapPlanningFetchTaskResponse,
  planningFetchTaskRowId,
  normalizeTaskType,
  resolveTaskName,
} from '../../utils/utils';
import {
  fetchAllTasks,
  fetchAllLinks,
  fetchTask,
  updateTasks,
  updateTaskMeta,
  convertTaskToWBS,
  addNewTask,
  createNewProject,
  deleteTask,
  createTaskDependency,
  deleteTaskDependency,
  fetchProjectCalendars,
  fetchProjectDhtmlxCalendars,
  exportProjectFile,
  exportLookAheadExcel,
  exportLookAheadPdf,
  exportScheduleExcel,
  recalculateProjectSchedule,
  reassignLocationToTask,
  type ScheduleRecalculateEngine,
  type UpdateTaskMetaItem,
} from '../../api/schedulingApis';
import { logPerfSummary } from '../../stores';
import { perfMonitor } from '../../utils/performanceMarkers';
import type { TaskSnapshot, LinkSnapshot } from '../../stores/types';
import { TaskPanel } from './components/TaskPanel';
import TaskDetailsDrawer from './components/TaskDetailsDrawer';
import { AssignUsersDrawer } from './components/assignments/AssignUsersDrawer';
import { AssignBulkSelectionBar } from './components/assignments/AssignBulkSelectionBar';
import {
  MOCK_ASSIGN_LOCATIONS,
  MOCK_ASSIGN_TASKS,
  MOCK_ASSIGN_USERS,
  createMockAssignments,
} from './components/assignments/assignUsersMockData';
import { flattenAssignLocationOptions, buildLocationPathById } from './components/assignments/flattenAssignLocations';
import {
  buildAssignTargetChipsFromTaskIds,
  buildAssigneeAvatarsHtml,
  formatTaskAssigneesSummary,
  listAssigneeFilterOptions,
  resolveAssignmentsForTask,
} from './components/assignments/assignDisplayUtils';
import type {
  AssignTargetChip,
  AssignTaskOption,
  AssignUserOption,
  AssignUsersDrawerMode,
  AssignUsersScope,
} from './components/assignments/assignUsersTypes';
import {
  fetchUserAssignments,
  type PlanningUserAssignment,
} from '../../api/userAssignmentApis';
import { fetchProjectAssignUsers } from '../../api/projectUsersApi';
import { LocationHierarchyModal } from '../../location-hierarchy';
import { LocationHierarchyFloatingPanel } from '../../location-hierarchy-floating';
import {
  PredecessorTaskPickerPanel,
  loadPredecessorGridShell,
  savePredecessorGridShell,
  type PredecessorGridShell,
} from './components/predecessor-picker';
import { getEnabledLocationIds, getEffectiveLocationIdForDisplay } from '../../location-hierarchy/locationScopeUtils';
import { useLocationsTreeStore } from '../../stores/locationsTreeStore';
import type { LocationTreeNode } from '../../api/schedulingApis';
import { ZoomControl, type ZoomLevel } from './components/ZoomControl';
import {
  GanttViewModeToggle,
  GanttTargetsPanel,
  applyWorkingCalendarToColumns,
  buildTargetsLookAheadWindow,
  formatLookAheadExportWindowCaption,
  lookAheadPeriodChoices,
  collectLookAheadLeafTaskIds,
  collectTargetsModeVisibleTaskIds,
  useGanttTargetsVisibleRows,
  useProjectWorkingCalendar,
  useTaskTargetsData,
  type GanttViewMode,
} from './components/targets';
import {
  loadGanttVisibleOptionalColumns,
  saveGanttVisibleOptionalColumns,
} from '../../gantt/ganttVisibleOptionalColumns';
import { ganttToolbarButtonProps } from './components/ganttToolbarStyles';
import { GanttLiveScheduleOffModal } from './components/GanttLiveScheduleOffModal';
import { GanttExportProgress } from './components/GanttExportProgress';
import {
  buildDhtmlxBaselinesForCompare,
  computeCurrentVsBaselineVariance,
  formatVarianceDays,
  listAvailableBaselineOptions,
  summarizeCurrentVsBaseline,
} from '../../gantt/baselineCompareUtils';
import {
  GANTT_AUTO_SCHEDULE_CHANGED_EVENT,
  readAutoSchedulePreference,
  readLiveScheduleOffModalSkipPreference,
  writeAutoSchedulePreference,
} from '../../gantt/autoSchedulePreference';
import {
  readScheduleRecalcEnginePreference,
  writeScheduleRecalcEnginePreference,
  type ScheduleRecalcEnginePreference,
} from '../../gantt/scheduleRecalcEnginePreference';
import {
  consumeGanttForceRefetch,
  invalidateGanttProjectCaches,
} from '../../gantt/ganttCacheLifecycle';
import {
  clearGanttSessionSnapshot,
  loadGanttSessionSnapshot,
  saveGanttSessionSnapshot,
} from '../../gantt/ganttSessionSnapshot';
import {
  applyCalendarDayKeepingTime,
  formatGanttDateFromStored,
  formatGanttDateLabel,
  sameCalendarDay,
} from '../../gantt/ganttDateFormat';
import {
  applyInclusiveDayToDateEditor,
  installGanttTypedDateEditor,
  resolveDateEditorSeedDay,
} from '../../gantt/ganttDateInlineEditor';
import { isInsideGanttInlineEditor } from '../../gantt/ganttInlineEditorOutsideClick';
import {
  applyInterimGlobalWorkTime,
  registerProjectDhtmlxCalendars,
} from '../../gantt/dhtmlxProjectCalendars';
import {
  buildDhtmlxScheduleFields,
  exclusiveEndToInclusiveFinishWallClock,
} from '../../gantt/mspInclusiveEndBridge';
import {
  columnKeysFromTaskPatch,
  wrapPendingChangeCell,
} from '../../gantt/editedFieldKeys';
import { syncVisiblePendingRowClasses } from '../../gantt/ganttPendingHighlightPaint';
import { syncAssignSelectCheckboxes } from '../../gantt/ganttAssignSelectPaint';
import { syncPickerActivePaint } from '../../gantt/ganttPickerActivePaint';
import {
  BULK_TASK_META_CHUNK_SIZE,
  classifyUnknownTaskTypes,
  type ClassifyUnknownTaskTypesResult,
} from '../../gantt/classifyUnknownTaskTypes';
import {
  buildTodayMarker,
  isTodayScaleCell,
} from '../../gantt/ganttTodayMarker';
import {
  ganttDayScaleCellClasses,
  type GanttWorkTimeApi,
} from '../../gantt/ganttNonWorkingScaleDay';
import {
  loadShowGanttChartPreference,
  saveShowGanttChartPreference,
} from '../../gantt/ganttShowChartPreference';
import { getGanttChartCssVariables } from '../../theme';
import { getDhtmlxGanttThemeName } from '../../theme/dhtmlxGanttTheme';
import { useDhtmlxGanttColorScheme } from '../../hooks/useDhtmlxGanttColorScheme';
import { useGanttLeaveGuard } from '../../hooks/useGanttLeaveGuard';
import {
  GANTT_LEAVE_CONFIRM_Z_INDEX,
  GanttLeaveConfirmModal,
} from './components/GanttLeaveConfirmModal';
import { useModalContainer } from '../../contexts/ModalContainerContext';
import { AddTaskModal } from './components/AddTaskModal';
import { deferAfterPaint, deferWhenIdle, yieldForPaint } from '../../utils/deferAfterPaint';
import { DeleteTaskConfirmModal } from './components/DeleteTaskConfirmModal';
import {
  SetProjectBaselineModal,
  type SetBaselineConfirmOptions,
  type SetProjectBaselineStep,
} from './components/SetProjectBaselineModal';
import { ActiveBaselineRevisionBanner } from './components/ActiveBaselineRevisionBanner';
import {
  approveBaselineRevision,
  createBaselineRevisionDraft,
  createProjectBaseline,
  discardBaselineRevision,
  fetchBaselineRevisionHistory,
  refreshBaselineRevision,
  rejectBaselineRevision,
  submitBaselineRevision,
} from '../../api/baselineApis';
import { collectSoftDeletedBaselineIndices } from '../schedule-baselines/logic/baselineHistoryUtils';
import { useActiveBaselineRevision } from '../../hooks/useActiveBaselineRevision';
import {
  invalidateRequestTrackingCache,
  useActiveProjectBaseline,
} from '../request-tracking';
import { useRequestTrackingModal } from '../../contexts/RequestTrackingModalContext';
import {
  GanttBusyOverlay,
  resolveGanttBusyOverlayState,
  type GanttLoaderMark,
} from './components/GanttBusyOverlay';
import {
  buildAddNewTaskRequest,
  buildInitialRootTaskSetup,
  collectAncestorTaskIds,
  findPlanningShellForUiRoot,
  getDhtmlxParentId,
  computeDurationFromStartAndFinish,
  isPlanningShellTask,
  mergeCreatePayloadWithSnapshot,
  nextClientLinkId,
  pickProjectCalendarId,
  projectHasUiRootTask,
  isLikelyAutoSeedOnlySchedule,
  resolveCalendarIdSync,
  resolveDefaultCalendarId,
  resolvePredecessorTaskIds,
  sanitizeCreateTaskPayload,
  taskHasChildren,
  taskSnapshotFromCreateRequest,
  type NewTaskFormValues,
} from '../../gantt/addTaskUtils';
import {
  repopulateGanttLinkPointers,
  findGanttLinkByEndpoints,
  mergeLinkSnapshotsFromGantt,
  resolvePredecessorAffectedLinkIds,
  applyPredecessorInputToStore,
  buildPredecessorDisplayText,
  type PredecessorLinkChange,
} from '../../gantt/taskLinkDisplayUtils';
import { collectCriticalPathTaskIds, collectCriticalPathVisibleTaskIds, summarizeCriticalPath } from '../../gantt/criticalPathFilter';
import { syncVisibleCriticalHighlightClasses } from '../../gantt/ganttCriticalHighlightPaint';
import { buildGanttColumns, type ColumnFilterValues } from '../../gantt/columns';
import {
  collectColumnFilterVisibleTaskIds,
  getActiveFilterColumnIds,
  hasActiveColumnFilters,
} from '../../gantt/columnFilters';
import { getFilterableColumnDefs } from '../../gantt/columns/registry';
import { GanttColumnFilterBar } from './components/GanttColumnFilterBar';
import { GanttColumnFilterStrip } from './components/GanttColumnFilterStrip';
import { GanttCriticalPathStrip } from './components/GanttCriticalPathStrip';

import { validatePredecessorLink } from '../../gantt/predecessorValidation';
import { buildSchedulePatchFromGanttTask } from '../../gantt/scheduleFieldDiff';
import {
  buildGanttLinkEndpointIndex,
  linkGraphsEquivalent,
} from '../../gantt/ganttLinkIndex';
import {
  formatDependencyLinkToken,
  parseDependencyToken,
  dependencyTokenToDhtmlxType,
} from '../../gantt/dependencyDisplayUtils';
import {
  GANTT_SAVE_PARALLEL_CHUNK_SIZE,
  mapInChunks,
} from '../../gantt/saveBatchUtils';
import {
  logAutoscheduleDirtyRatio,
  markGanttPerf,
  measureGanttPerf,
} from '../../gantt/ganttPerfGuards';
import {
  applyScheduleGridColumnLayout,
  disableGanttGridContentScroll,
  fitGanttColumnsToContent,
  rememberScheduleGridWidth,
} from '../../gantt/fitGanttColumnsToContent';
import { ensureEmptyGanttRootSchedule } from '../../gantt/ensureEmptyGanttRoot';
import { filterUpdateIdsExcludingPending } from '../../gantt/filterUpdateIdsExcludingPending';
import {
  applyFixedColumnWidths,
  chooseGridColumnLayoutMode,
  DEFAULT_GANTT_COLUMN_WIDTHS,
  FLEX_GANTT_COLUMN_NAME,
  hasPinnedGanttColumnWidths,
  isPersistableGanttColumnName,
  loadGanttColumnWidths,
  resolveGanttColumnWidth,
  saveGanttColumnWidths,
  sessionColumnWidthMap,
  sumVisibleColumnWidths,
} from '../../gantt/ganttColumnWidths';
import {
  gridSharePercentFromWidths,
  resolveScheduleGridWidth,
  saveGanttGridSplit,
} from '../../gantt/ganttGridSplit';
import { useFeatureTipsOptional } from '../feature-tips';
import { ScheduleReadinessBanner } from '../schedule-readiness';
import { APP_ROUTES } from '../../navigation/appRoutes';
import {
  ENABLE_TARGETS_QUANTITY_PANEL,
  USE_FLOATING_LOCATION_PANEL,
} from './ganttPageFlags';
import { useGanttScheduleLoad } from './hooks/useGanttScheduleLoad';
import { useGanttSessionSync } from './hooks/useGanttSessionSync';
import { useGanttChrome } from './hooks/useGanttChrome';
import { useGanttAssignments, useGanttAssignBulk } from './hooks/useGanttAssignments';
import { useGanttPredecessors } from './hooks/useGanttPredecessors';
import { useGanttBaselines } from './hooks/useGanttBaselines';
import { useGanttTaskCommands } from './hooks/useGanttTaskCommands';
import { useGanttEngine } from './hooks/useGanttEngine';
import { GanttPageToolbar } from './containers/GanttPageToolbar';
import { GanttPageDialogs } from './containers/GanttPageDialogs';
import { GanttChartPane } from './containers/GanttChartPane';

/** Escape for use in HTML so we can safely embed task id and text in Location cell. */
function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Escape text for XML element/attribute content. */
function escapeXml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Inner Gantt component that uses the Zustand store
 */
function ZustandGanttInner({
  onConfirmLeaveRef,
}: {
  onConfirmLeaveRef: React.MutableRefObject<() => Promise<void>>;
}) {
  const store = useTaskStore();
  const storeLoading = useTaskStoreLoading();
  const sessionMetadata = useSessionMetadata();
  const modalContainer = useModalContainer();
  const pendingChangeTaskIds = usePendingChangeTaskIds();
  const pendingEditedFields = usePendingEditedFields();
  const pendingEditedFieldsRef = useRef(pendingEditedFields);
  pendingEditedFieldsRef.current = pendingEditedFields;
  const pendingChangeTaskIdsRef = useRef(pendingChangeTaskIds);
  pendingChangeTaskIdsRef.current = pendingChangeTaskIds;
  const theme = useMantineTheme();
  const featureTips = useFeatureTipsOptional();
  const [searchParams, setSearchParams] = useSearchParams();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const canTaskCreate = useCanSchedulerAction('taskCreate');
  const canTaskEdit = useCanSchedulerAction('taskEdit');
  const canTaskDelete = useCanSchedulerAction('taskDelete');
  const canTaskSetBaseline = useCanSchedulerAction('taskSetBaseline');
  const canMutateTasks = canTaskCreate || canTaskEdit || canTaskDelete;
  /** Create or edit on tasks (edit satisfies create via privilege ladder). */
  const canSetBaseline = canTaskSetBaseline || canTaskEdit;
  const [taskDetailsDrawerEnabled, setTaskDetailsDrawerEnabled] = useState(true);
  const [scrollToLinkedIssuesRequest, setScrollToLinkedIssuesRequest] = useState(0);
  const [trackTaskLoadingTaskId, setTrackTaskLoadingTaskId] = useState<string | null>(null);
  const ganttRef = useRef<ReactGanttRef>(null);
  const projectId = getProjectId() || '';
  const projectDisplayName = getProjectDisplayName();
  const autoScheduleApplyingRef = useRef(false);
  const autoScheduleMuteSafetyTimerRef = useRef<number | null>(null);
  /** Skip mass refreshTask when cascade marks thousands of pending rows. */
  const skipPendingHighlightRefreshRef = useRef(false);
  const lastLinkPointersRevisionRef = useRef(-1);
  /** Suppress onAfterLinkAdd/Delete while syncing panel-driven predecessor edits into gantt. */
  const predecessorLinkCommitInProgressRef = useRef(false);
  const resolvePredecessorWbsCodeRef = useRef<(taskId: string) => string | null>(() => null);
  /**
   * Suppress onAfterTaskUpdate while intentional location/taskType meta patches run —
   * those save via updateTaskMeta immediately and must not enter Save-to-Server pending.
   */
  const taskMetaSyncInProgressRef = useRef(false);
  /** While predecessor inline editor is open, prefer Sr No from getWBSCode (restore on edit end). */
  const predecessorSrNoCodeOverrideRef = useRef<{
    active: boolean;
    original: ((task: unknown) => string) | null;
  }>({ active: false, original: null });
  /** Allow Task name inline editor only from double-click (not single-click). */
  const taskNameInlineEditAllowedRef = useRef(false);
  /** Next onAfterTaskUpdate for end_date came from the grid End editor (inclusive day). */
  const endDateEditorCommitRef = useRef(false);
  /**
   * Optional recovery bump to force DHTMLX clearAll+parse from store-derived parents.
   * Not used on Schedule↔Targets anymore (display filter only — reparsing 15k tasks was too slow).
   */
  const [ganttTasksEpoch] = useState(0);
  const [calendarReady, setCalendarReady] = useState(false);
  const [autoScheduleEnabled, setAutoScheduleEnabled] = useState(readAutoSchedulePreference);
  const criticalHighlightPaintRef = useRef<{ ids: Set<string>; overdue: boolean }>({
    ids: new Set(),
    overdue: false,
  });
  const openTaskDetailsPanelRef = useRef<
    (id: string | number, options?: { scrollToLinkedIssues?: boolean }) => void
  >(() => {});
  const openAddTaskModalRef = useRef<(parentId: string | null) => void>(() => {});
  const canTaskCreateRef = useRef(canTaskCreate);
  canTaskCreateRef.current = canTaskCreate;
  const [manualRecalculateLoading, setManualRecalculateLoading] = useState(false);
  /** Explains Recalculate schedule after the user turns Live Schedule off. */
  const [liveScheduleOffModalOpen, setLiveScheduleOffModalOpen] = useState(false);
  const [scheduleRecalcLoading, setScheduleRecalcLoading] = useState(false);
  /** User-resized grid column widths for this tab session. */
  const columnWidthsRef = useRef<Partial<Record<string, number>>>({});
  /**
   * Track drag state and accumulated changes (declared early so column templates
   * can close over the ref without TDZ issues).
   */
  const isDragging = useRef(false);
  const accumulatedChanges = useRef(new Map<string, Partial<TaskSnapshot>>());
  const dumpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Skip protected-width re-apply while the user is dragging a column border. */
  const columnResizeInProgressRef = useRef(false);
  const [columnWidthsVersion, setColumnWidthsVersion] = useState(0);
  const [recalcEngine, setRecalcEngine] = useState<ScheduleRecalcEnginePreference>(
    readScheduleRecalcEnginePreference,
  );





  const { openRequestTracker } = useRequestTrackingModal();
  const {
    baselineInProgress,
    refresh: refreshBaselineGuard,
  } = useActiveProjectBaseline(projectId, 10000);
  const {
    activeRevision,
    refresh: refreshActiveRevision,
  } = useActiveBaselineRevision(projectId);



  /** Close any open inline editor so the Location cell never stays as a blank input. */
  const closeLocationInlineEditor = useCallback(() => {
    const g = ganttRef.current?.instance;
    if (g?.ext?.inlineEditors?.hide) {
      try {
        g.ext.inlineEditors.hide();
      } catch (_) {
        // ignore
      }
    }
  }, []);

  /** Hide stale inline editors (date/pred/etc.) after refetch or when editing should end. */
  const dismissGanttInlineEditing = useCallback(() => {
    const g = ganttRef.current?.instance as {
      ext?: { inlineEditors?: { isVisible?: () => boolean; hide?: () => void } };
    } | undefined;
    try {
      if (g?.ext?.inlineEditors?.isVisible?.()) {
        g.ext.inlineEditors.hide?.();
      }
    } catch {
      // ignore
    }
  }, []);

  /**
   * Commit if the value changed, otherwise just hide.
   * DHTMLX `save()` already does this — use it for outside-click dismiss.
   */
  const commitOrDismissGanttInlineEditing = useCallback(() => {
    const g = ganttRef.current?.instance as {
      ext?: {
        inlineEditors?: {
          isVisible?: () => boolean;
          save?: () => void;
          hide?: () => void;
        };
      };
    } | undefined;
    try {
      if (!g?.ext?.inlineEditors?.isVisible?.()) return;
      g.ext.inlineEditors.save?.();
    } catch {
      try {
        g?.ext?.inlineEditors?.hide?.();
      } catch {
        // ignore
      }
    }
  }, []);

  /**
   * DHTMLX only ends inline edit on `onEmptyClick` or when opening another editable cell.
   * Clicks on non-editable cells, the timeline, toolbar, or page chrome leave the editor stuck —
   * dismiss (save if dirty) on any pointerdown outside the editor UI.
   */
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (isInsideGanttInlineEditor(e.target)) return;
      commitOrDismissGanttInlineEditing();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [commitOrDismissGanttInlineEditing]);

  // Use reactive hook for tasks
  const dhtmlxTasks = useDhtmlxTasks();
  const linksRevision = useLinksRevision();
  const links = store.getLinks();
  /** Revision-keyed O(1) pred/succ lookups for optional grid columns. */
  const predSuccLinkIndex = useMemo(
    () => buildGanttLinkEndpointIndex(links),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- links identity follows linksRevision
    [linksRevision],
  );
  const selectedTaskId = useSelectedTaskId();
  const isPanelOpen = !!selectedTaskId;

  // Location tree from task store (used for grid: locationId → name). Synced when we fetch or hydrate.
  const locationTree = useLocationTree();
  /** When opening panel from a task's location cell: only these location IDs are selectable (null = all). */
  const locationIdToName = useMemo(() => {
    const map = new Map<string, string>();
    function walk(nodes: LocationTreeNode[]) {
      for (const n of nodes) {
        if (n.location_id && n.name != null) map.set(String(n.location_id), n.name);
        if (n.child?.length) walk(n.child);
      }
    }
    if (locationTree?.length) walk(locationTree);
    return map;
  }, [locationTree]);
  const locationIdToNameRef = useRef(locationIdToName);
  locationIdToNameRef.current = locationIdToName;

  /**
   * Bump after location/taskType meta writes so inherited location display recomputes.
   * Field-only store patches keep `dhtmlxTasks` identity stable, so this epoch is required.
   */
  const [locationDisplayEpoch, setLocationDisplayEpoch] = useState(0);

  /** Task id -> effective location id for display (own or derived from nearest Location-type ancestor). */
  const effectiveLocationIdByTaskId = useMemo(() => {
    const map = new Map<string, string>();
    const tasks = store.getTasks();
    const getTask = store.getTask.bind(store);
    for (const t of tasks) {
      const eff = getEffectiveLocationIdForDisplay(getTask, t.id);
      if (eff) map.set(t.id, eff);
    }
    return map;
  }, [store, dhtmlxTasks, locationDisplayEpoch]);
  const effectiveLocationIdByTaskIdRef = useRef(effectiveLocationIdByTaskId);
  effectiveLocationIdByTaskIdRef.current = effectiveLocationIdByTaskId;

  /** Leaf task ids that have unitActivityDetails (tracked as unit-activities). */
  const trackedLeafTaskIds = useMemo(() => {
    const allTasks = store.getTasks();
    const parentIds = new Set<string>();
    for (const t of allTasks) {
      if (t.parentTaskId) parentIds.add(String(t.parentTaskId));
    }
    const tracked = new Set<string>();
    for (const t of allTasks) {
      if (parentIds.has(String(t.id))) continue;
      const details = t.unitActivityDetails;
      if (Array.isArray(details) && details.length > 0) {
        tracked.add(String(t.id));
      }
    }
    return tracked;
  }, [store, dhtmlxTasks]);

  /** Parent task ids that have at least one child (delete disabled for these rows). */
  const parentIdsWithChildren = useMemo(() => {
    const parentIds = new Set<string>();
    for (const t of store.getTasks()) {
      if (t.parentTaskId) parentIds.add(String(t.parentTaskId));
    }
    return parentIds;
  }, [store, dhtmlxTasks]);

  const targetsGateRef = useRef({ dirtyCount: 0, discardDrafts: () => {} });
  const {
    zoomLevel,
    viewMode,
    lookAheadPeriod,
    setLookAheadPeriod,
    targetsShowFullWbs,
    setTargetsShowFullWbs,
    targetsFocusExpandKeyRef,
    viewSwitchBusy,
    setViewSwitchBusy,
    viewSwitchBusyLabel,
    viewSwitchMark,
    showCriticalPathOnly,
    setShowCriticalPathOnly,
    highlightCriticalPath,
    setHighlightCriticalPath,
    criticalFilterEpoch,
    setCriticalFilterEpoch,
    criticalFocusExpandKeyRef,
    taskSearchQuery,
    setTaskSearchQuery,
    debouncedTaskSearch,
    columnFilterValues,
    setColumnFilterValues,
    debouncedColumnFilterValues,
    columnFilterExpandKeyRef,
    taskSearchInputRef,
    searchFocusExpandKeyRef,
    taskSearchExpanded,
    setTaskSearchExpanded,
    showGanttChart,
    setShowGanttChart,
    keyboardShortcutsOpen,
    setKeyboardShortcutsOpen,
    exporting,
    setExporting,
    exportingExcel,
    setExportingExcel,
    exportProgress,
    setExportProgress,
    visibleOptionalColumns,
    setVisibleOptionalColumns,
    collapsedBranchIds,
    setCollapsedBranchIds,
    collapsedBranchIdsRef,
    targetsDiscardPending,
    setTargetsDiscardPending,
    applyViewModeChange,
    applyZoomLevelChange,
    handleViewModeChange,
    handleZoomLevelChange,
    handleTargetsDiscardCancel,
    handleTargetsDiscardConfirm,
    handleExpandAllToggle,
    allTasksExpanded,
  } = useGanttChrome({
    projectId,
    ganttRef,
    featureTips,
    searchParams,
    setSearchParams,
    parentIdsWithChildren,
    targetsGateRef,
  });


  const {
    assignDrawerOpen,
    setAssignDrawerOpen,
    assignDrawerMode,
    setAssignDrawerMode,
    assignPresentation,
    setAssignPresentation,
    assignTargetsLocked,
    setAssignTargetsLocked,
    assignInitialScope,
    setAssignInitialScope,
    assignInitialTaskTargets,
    setAssignInitialTaskTargets,
    mockAssignments,
    setMockAssignments,
    liveAssignments,
    setLiveAssignments,
    assignUserOptions,
    setAssignUserOptions,
    assignUsersLoading,
    setAssignUsersLoading,
    assignAssignmentsLoading,
    setAssignAssignmentsLoading,
    selectedAssignTaskIds,
    setSelectedAssignTaskIds,
    assignMockEnabled,
    assignMockAutoOpenedRef,
    selectedAssignTaskIdsRef,
    assignLocationOptions,
    locationPathById,
    assignTaskCatalog,
    buildSelectedTaskAssignChip,
    selectedTaskAssigneesSummary,
    refreshLiveAssignments,
    openAssignUsersDrawer,
    clearAssignSelection,
    toggleAssignTaskSelected,
  } = useGanttAssignments({
    searchParams,
    locationTree,
    store,
    effectiveLocationIdByTaskId,
    dhtmlxTasks,
    selectedTaskId,
    locationIdToName,
    projectId,
  });

  const hasUiRootTask = useMemo(
    () => projectHasUiRootTask(store.getTasks()),
    [store, dhtmlxTasks],
  );

  const defaultCalendarId = useMemo(
    () => resolveDefaultCalendarId(store.getTasks()),
    [store, dhtmlxTasks],
  );

  /** Project default calendar (from tasks or one-time calendar list fetch on mount). */
  const [projectCalendarId, setProjectCalendarId] = useState('');

  useEffect(() => {
    if (!projectId) return;
    if (defaultCalendarId) {
      setProjectCalendarId(defaultCalendarId);
      return;
    }
    let cancelled = false;
    void fetchProjectCalendars(projectId).then((calendars) => {
      if (cancelled) return;
      setProjectCalendarId(pickProjectCalendarId(calendars));
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, defaultCalendarId]);

  const {
    lookAheadConfig,
    error,
    setError,
    loadingProgress,
    setLoadingProgress,
    readinessDataReady,
    ensureGanttRootAttemptedRef,
    reloadScheduleFromServer,
    handleInvalidateAndFetch,
  } = useGanttScheduleLoad({
    store,
    ganttRef,
    projectId,
    dismissGanttInlineEditing,
    canTaskCreate,
    projectCalendarId,
    defaultCalendarId,
    refreshActiveRevision,
  });

  useEffect(() => {
    if (!projectId) {
      columnWidthsRef.current = {};
      ensureGanttRootAttemptedRef.current = null;
      return;
    }
    ensureGanttRootAttemptedRef.current = null;
    columnWidthsRef.current = { ...(loadGanttColumnWidths(projectId)?.widths ?? {}) };
    setColumnWidthsVersion((v) => v + 1);
  }, [projectId]);

  const scheduleBackgroundPersist = useCallback(
    (reason: string, patchedTaskIds?: string[]) => {
      if (!projectId) return;
      if (patchedTaskIds && patchedTaskIds.length === 0) return;
      deferWhenIdle(() => {
        // Always patch when ids are provided (any size) — never full-persist on live cascades.
        const usePatch = patchedTaskIds != null && patchedTaskIds.length > 0;
        const run = usePatch
          ? () => store.persistTaskPatches(patchedTaskIds)
          : () => store.persist(projectId);
        void run().catch((err) => {
          console.error(`[ZustandGantt] Background persist failed (${reason}):`, err);
        });
      });
    },
    [store, projectId],
  );

  /** Server-side project recalculate (MPXJ / CPM), then reload schedule from API. */
  const handleServerScheduleRecalculate = useCallback(
    async (engine: ScheduleRecalculateEngine) => {
      if (!projectId || scheduleRecalcLoading) return;
      setScheduleRecalcLoading(true);
      try {
        await recalculateProjectSchedule({
          projectId,
          engine,
        });
        await reloadScheduleFromServer({
          reason: 'server-recalc',
          invalidateLocalCache: true,
        });
      } catch {
        // Error toast handled in API helper.
      } finally {
        setScheduleRecalcLoading(false);
      }
    },
    [projectId, scheduleRecalcLoading, reloadScheduleFromServer],
  );

  const {
    saving,
    discarding,
    localProcessing,
    discardConfirmOpen,
    setDiscardConfirmOpen,
    saveConfirmOpen,
    setSaveConfirmOpen,
    beginLocalProcessing,
    endLocalProcessing,
    handleSaveToBackend,
    handleConfirmSaveToBackend,
    handleDiscardChanges,
  } = useGanttSessionSync({
    store,
    projectId,
    onConfirmLeaveRef,
    canMutateTasks,
    activeRevision,
    refreshActiveRevision,
    scheduleBackgroundPersist,
    reloadScheduleFromServer,
    setLoadingProgress,
    recalcEngine,
    handleServerScheduleRecalculate,
  });


  const {
    predecessorPickerOpen,
    setPredecessorPickerOpen,
    predecessorPickerTaskId,
    setPredecessorPickerTaskId,
    predecessorPickerMode,
    setPredecessorPickerMode,
    predecessorPickerInputTick,
    setPredecessorPickerInputTick,
    predecessorGridShell,
    setPredecessorGridShell,
    predecessorPickerTaskIdRef,
    predecessorPanelDraftRef,
    openPredecessorPickerRef,
    commitPredecessorLinkChanges,
    closePredecessorPicker,
    handleTaskPanelPredecessorUpdate,
    predecessorPickerCurrentInput,
    handlePredecessorPickerApplyInput,
  } = useGanttPredecessors({
    ganttRef,
    predecessorLinkCommitInProgressRef,
    store,
    autoScheduleEnabled,
    scheduleBackgroundPersist,
    dhtmlxTasks,
    resolvePredecessorWbsCodeRef,
  });

  const {
    baselineModalOpen,
    setBaselineModalOpen,
    baselineModalStep,
    setBaselineModalStep,
    baselineSubmitting,
    setBaselineSubmitting,
    baselineModalBlockedMessage,
    setBaselineModalBlockedMessage,
    revisionActionBusy,
    setRevisionActionBusy,
    compareBaselinesEnabled,
    setCompareBaselinesEnabled,
    compareBaselineIndex,
    setCompareBaselineIndex,
    softDeletedBaselineIndices,
    setSoftDeletedBaselineIndices,
    baselineWasInProgressRef,
    baselineStartPendingRef,
    rejectedSnapshotRefreshAttemptedRef,
    baselineCompareOptions,
    baselineVarianceSummary,
    compareBaselineLabel,
    closeBaselineModal,
    handleOpenSetBaseline,
    handleBaselineSaveAndContinue,
    handleConfirmCreateBaseline,
    runRevisionAction,
    handleSubmitActiveRevision,
    handleRefreshActiveRevision,
    handleDiscardActiveRevision,
    handleApproveActiveRevision,
    handleRejectActiveRevision,
  } = useGanttBaselines({
    store,
    dhtmlxTasks,
    projectId,
    saving,
    canSetBaseline,
    discarding,
    exporting,
    refreshActiveRevision,
    handleSaveToBackend,
    openRequestTracker,
    refreshBaselineGuard,
    setLoadingProgress,
    reloadScheduleFromServer,
    activeRevision,
    baselineInProgress,
  });

  /** Patch a single Gantt row in-place so the grid updates without a full React tasks refresh. */
  const patchGanttTaskFields = useCallback((taskId: string, fields: Record<string, unknown>) => {
    const gantt = ganttRef.current?.instance as {
      getTask?: (id: string) => Record<string, unknown>;
      refreshTask?: (id: string) => void;
      updateTask?: (id: string) => void;
    } | undefined;
    if (!gantt?.getTask) return;
    try {
      const task = gantt.getTask(taskId);
      if (!task) return;
      Object.assign(task, fields);
      if (gantt.updateTask) gantt.updateTask(taskId);
      else gantt.refreshTask?.(taskId);
    } catch {
      // Task may not exist during initial parse.
    }
  }, []);

  /**
   * After location meta changes, recompute inherited location ids and refresh only
   * currently visible rows (O(visible) — not O(all descendants)).
   */
  const syncInheritedLocationDisplay = useCallback(() => {
    const map = new Map<string, string>();
    const getTask = store.getTask.bind(store);
    for (const t of store.getTasks()) {
      const eff = getEffectiveLocationIdForDisplay(getTask, t.id);
      if (eff) map.set(t.id, eff);
    }
    effectiveLocationIdByTaskIdRef.current = map;
    setLocationDisplayEpoch((n) => n + 1);

    const gantt = ganttRef.current?.instance as {
      isTaskExists?: (id: string) => boolean;
      refreshTask?: (id: string) => void;
      $container?: HTMLElement;
    } | undefined;
    const ganttRoot =
      gantt?.$container ??
      (document.querySelector('.fbt-gantt-root') as HTMLElement | null);
    if (!gantt?.refreshTask || !ganttRoot) return;
    ganttRoot.querySelectorAll('.gantt_row[data-task-id]').forEach((node) => {
      const id = (node as HTMLElement).getAttribute('data-task-id');
      if (id && gantt.isTaskExists?.(id)) gantt.refreshTask?.(id);
    });
  }, [store]);

  const syncTaskMetaInBackground = useCallback(
    (
      payload: { taskId: string; type: string; locationId?: string }[],
      options?: { onFailure?: () => void },
    ) => {
      if (payload.length === 0) return;
      void updateTaskMeta(payload)
        .then(() => {
          scheduleBackgroundPersist('task-meta');
        })
        .catch(() => {
          // Error toast already shown by updateTaskMeta (unless silent).
          options?.onFailure?.();
          scheduleBackgroundPersist('task-meta');
        });
    },
    [scheduleBackgroundPersist],
  );

  const buildTaskMetaItem = useCallback(
    (taskId: string, taskType: string) => {
      const type = normalizeTaskType(taskType) ?? 'unknown';
      const item: { taskId: string; type: string; locationId?: string } = { taskId, type };
      if (type === 'location') {
        const task = store.getTask(taskId);
        const getTask = store.getTask.bind(store);
        const locationId =
          task?.locationId != null && String(task.locationId).trim() !== ''
            ? task.locationId
            : getEffectiveLocationIdForDisplay(getTask, taskId);
        item.locationId = locationId ?? '';
      }
      return item;
    },
    [store],
  );


  const {
    reassignLocationsLoading,
    setReassignLocationsLoading,
    reassignConfirmOpen,
    setReassignConfirmOpen,
    bulkUnknownTypeConfirmOpen,
    setBulkUnknownTypeConfirmOpen,
    bulkUnknownTypePreview,
    setBulkUnknownTypePreview,
    bulkUnknownTypeLoading,
    setBulkUnknownTypeLoading,
    locationModalOpen,
    setLocationModalOpen,
    locationModalTaskId,
    setLocationModalTaskId,
    addTaskModalOpen,
    setAddTaskModalOpen,
    addTaskParentId,
    setAddTaskParentId,
    addTaskSubmitting,
    setAddTaskSubmitting,
    addTaskError,
    setAddTaskError,
    deleteTaskTarget,
    setDeleteTaskTarget,
    deleteTaskSubmitting,
    setDeleteTaskSubmitting,
    locationModalTaskIdRef,
    openLocationModalRef,
    addTaskParentIdRef,
    enabledLocationIds,
    openAddTaskModal,
    removeLinksForTask,
    closeAddTaskModal,
    handleCreateTask,
    handleConfirmDeleteTask,
    requestReassignLocations,
    runReassignLocations,
    getLocationNameById,
    confirmReassignLocations,
    requestBulkUnknownTaskTypes,
    confirmBulkUnknownTaskTypes,
    assignLocationToTask,
  } = useGanttTaskCommands({
    store,
    locationTree,
    projectId,
    defaultCalendarId,
    projectCalendarId,
    setProjectCalendarId,
    openAddTaskModalRef,
    ganttRef,
    setCollapsedBranchIds,
    scheduleBackgroundPersist,
    selectedTaskId,
    saving,
    discarding,
    reloadScheduleFromServer,
    handleSaveToBackend,
    canMutateTasks,
    beginLocalProcessing,
    endLocalProcessing,
    closeLocationInlineEditor,
    taskMetaSyncInProgressRef,
    patchGanttTaskFields,
    syncTaskMetaInBackground,
    syncInheritedLocationDisplay,
  });

  // Preload location tree when Gantt mounts so we can show location names from meta.locationId
  useEffect(() => {
    if (projectId) useLocationsTreeStore.getState().fetchTreeIfNeeded(projectId);
  }, [projectId]);

  /** Gantt chart + grid CSS variables (colors from src/theme/brand.ts). */
  const themeVars = useMemo(
    () => getGanttChartCssVariables(theme, colorScheme),
    [theme, colorScheme]
  );

  const ganttSkinTheme = getDhtmlxGanttThemeName(colorScheme);
  useDhtmlxGanttColorScheme(colorScheme, ganttRef);

  const plugins = useMemo(() => ({
    critical_path: true,
    auto_scheduling: true,
    marker: true,
    keyboard_navigation: true,
  }), []);

  const todayMarkers = useMemo(() => [buildTodayMarker(zoomLevel)], [zoomLevel]);

  const scrollGanttToToday = useCallback(() => {
    const gantt = ganttRef.current?.instance as {
      showDate?: (date: Date) => void;
    } | undefined;
    if (!gantt?.showDate) return;
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    gantt.showDate(today);
  }, []);

  /**
   * Manual time-scale zoom (day/week/month/year).
   * We update gantt.config.scales directly because gantt.ext.zoom is unreliable in this wrapper build.
   */
  useEffect(() => {
    const gantt = ganttRef.current?.instance as any;
    if (!gantt?.config) return;

    const level = zoomLevel;
    const dayScaleCss = (date: Date) =>
      ganttDayScaleCellClasses({
        date,
        zoomLevel: 'day',
        gantt: gantt as GanttWorkTimeApi,
        isToday: isTodayScaleCell(date, 'day'),
        kind: 'scale',
        applyNonWorking: true,
      });
    const cfgByLevel: Record<typeof level, { scales: any[]; min_column_width: number; scale_height: number }> = {
      day: {
        scale_height: 60,
        min_column_width: 30,
        scales: [
          { unit: 'month', step: 1, format: '%M %Y' },
          // Non-working grey only on the Day row — not via scale_cell_class (that also paints Month).
          { unit: 'day', step: 1, format: '%d', css: dayScaleCss },
        ],
      },
      week: {
        scale_height: 60,
        min_column_width: 80,
        scales: [
          { unit: 'month', step: 1, format: '%M %Y' },
          { unit: 'week', step: 1, format: 'W%W' },
        ],
      },
      month: {
        scale_height: 60,
        min_column_width: 80,
        scales: [
          { unit: 'year', step: 1, format: '%Y' },
          { unit: 'month', step: 1, format: '%M' },
        ],
      },
      year: {
        scale_height: 60,
        min_column_width: 60,
        scales: [{ unit: 'year', step: 1, format: '%Y' }],
      },
    };

    let cancelled = false;
    const next = cfgByLevel[level];
    gantt.config.scales = next.scales;
    gantt.config.min_column_width = next.min_column_width;
    gantt.config.scale_height = next.scale_height;
    gantt.setSizes?.();
    gantt.render?.();
    // Scale-only update — do NOT clearAll/parse (15k+ tasks). Hierarchy is stable
    // now that Targets no longer filters the task set.
    // Keep overlay up through follow-on filter/column work (especially in Targets).
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => {
          if (!cancelled) setViewSwitchBusy(false);
        }, 120);
      });
    });
    const safety = window.setTimeout(() => {
      if (!cancelled) setViewSwitchBusy(false);
    }, 8000);
    return () => {
      cancelled = true;
      window.clearTimeout(safety);
    };
  }, [zoomLevel]);

  /**
   * Working calendars for Live Schedule (DHTMLX):
   * 1) Interim global Mon–Fri for tasks with null calendarId
   * 2) Project calendars from /calendar/dhtmlx → addCalendar (per-task calendar_id)
   */
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40;
    let pollTimer: number | undefined;

    const finishReady = () => {
      if (!cancelled) setCalendarReady(true);
    };

    const applyCalendars = async () => {
      const gantt = ganttRef.current?.instance as any;
      if (!gantt?.setWorkTime || !gantt?.config) {
        attempts += 1;
        if (attempts < maxAttempts && !cancelled) {
          pollTimer = window.setTimeout(() => {
            void applyCalendars();
          }, 50);
        } else {
          finishReady();
        }
        return;
      }

      applyInterimGlobalWorkTime(gantt);

      if (projectId) {
        try {
          const calendars = await fetchProjectDhtmlxCalendars(projectId);
          if (cancelled) return;
          registerProjectDhtmlxCalendars(gantt, calendars);
        } catch (err) {
          console.warn('[Gantt] Project DHTMLX calendars failed; using global only', err);
        }
      }

      if (cancelled) return;
      gantt.render?.();
      finishReady();
    };

    setCalendarReady(false);
    pollTimer = window.setTimeout(() => {
      void applyCalendars();
    }, 0);

    return () => {
      cancelled = true;
      if (pollTimer != null) window.clearTimeout(pollTimer);
    };
  }, [projectId]);

  const applyAutoScheduleToGantt = useCallback((enabled: boolean) => {
    const gantt = ganttRef.current?.instance as { config?: { auto_scheduling?: boolean } } | undefined;
    if (gantt?.config) {
      gantt.config.auto_scheduling = enabled;
    }
  }, []);

  useEffect(() => {
    if (!calendarReady) return;
    applyAutoScheduleToGantt(autoScheduleEnabled);
  }, [autoScheduleEnabled, calendarReady, applyAutoScheduleToGantt]);

  useEffect(() => {
    const onPreferenceChanged = (event: Event) => {
      const enabled = (event as CustomEvent<{ enabled: boolean }>).detail?.enabled;
      if (typeof enabled !== 'boolean') return;
      setAutoScheduleEnabled(enabled);
      applyAutoScheduleToGantt(enabled);
    };
    window.addEventListener(GANTT_AUTO_SCHEDULE_CHANGED_EVENT, onPreferenceChanged);
    return () => window.removeEventListener(GANTT_AUTO_SCHEDULE_CHANGED_EVENT, onPreferenceChanged);
  }, [applyAutoScheduleToGantt]);

  const handleAutoScheduleToggle = useCallback(
    (enabled: boolean) => {
      setAutoScheduleEnabled(enabled);
      writeAutoSchedulePreference(enabled);
      applyAutoScheduleToGantt(enabled);
      if (!enabled) {
        if (!readLiveScheduleOffModalSkipPreference()) {
          setLiveScheduleOffModalOpen(true);
        }
        return;
      }
      notifications.show({
        title: 'Live Re-Scheduling (ON)',
        message: 'Linked tasks will update when you change dates, duration, or dependencies.',
        color: 'teal',
        autoClose: 4000,
      });
    },
    [applyAutoScheduleToGantt]
  );

  /** DHTMLX one-shot dependency recalc (used when no MPXJ/CPM preference is selected). */
  const handleDhtmlxManualRecalculate = useCallback(() => {
    const gantt = ganttRef.current?.instance as {
      autoSchedule?: (id?: string | number) => void;
      isTaskExists?: (id: string | number) => boolean;
      config?: { auto_scheduling?: boolean };
      render?: () => void;
    } | undefined;

    if (!gantt?.autoSchedule) {
      notifications.show({
        title: 'Recalculate unavailable',
        message: 'The Gantt chart is not ready yet. Try again in a moment.',
        color: 'red',
      });
      return;
    }

    const scopeId =
      selectedTaskId && gantt.isTaskExists?.(selectedTaskId) ? selectedTaskId : null;

    setManualRecalculateLoading(true);
    void (async () => {
      await beginLocalProcessing(
        'Recalculating schedule',
        'Updating dependent tasks. Large projects can take a few seconds.',
      );
      const prevAutoScheduling = gantt.config?.auto_scheduling;
      try {
        if (gantt.config) {
          gantt.config.auto_scheduling = true;
        }
        if (scopeId != null) {
          gantt.autoSchedule?.(scopeId);
        } else {
          gantt.autoSchedule?.();
        }
        gantt.render?.();
        notifications.show({
          title: 'Schedule recalculated',
          message:
            scopeId != null
              ? 'Dependent tasks were updated from the selected task.'
              : 'Dependent tasks were updated for the whole project.',
          color: 'teal',
          autoClose: 3000,
        });
      } catch (err) {
        console.error('[ZustandGantt] Manual recalculate failed:', err);
        notifications.show({
          title: 'Recalculate failed',
          message: 'Could not run schedule recalculation. Check dependencies and try again.',
          color: 'red',
        });
      } finally {
        if (gantt.config) {
          gantt.config.auto_scheduling = autoScheduleEnabled;
        } else if (prevAutoScheduling !== undefined) {
          applyAutoScheduleToGantt(prevAutoScheduling);
        }
        endLocalProcessing();
        setManualRecalculateLoading(false);
      }
    })();
  }, [applyAutoScheduleToGantt, autoScheduleEnabled, selectedTaskId, beginLocalProcessing, endLocalProcessing]);

  const visibleTaskIds = useMemo(
    () => new Set(dhtmlxTasks.map((task) => String(task.id))),
    [dhtmlxTasks],
  );

  const dhtmlxBaselines = useMemo(() => {
    if (!compareBaselinesEnabled || compareBaselineIndex == null) return [];
    return buildDhtmlxBaselinesForCompare(
      store.getTasks(),
      compareBaselineIndex,
      visibleTaskIds,
    );
  }, [compareBaselinesEnabled, compareBaselineIndex, store, dhtmlxTasks, visibleTaskIds]);

  const baselinesByTaskId = useMemo(() => {
    const map = new Map<string, typeof dhtmlxBaselines>();
    for (const baseline of dhtmlxBaselines) {
      const key = String(baseline.task_id);
      const list = map.get(key);
      if (list) list.push(baseline);
      else map.set(key, [baseline]);
    }
    return map;
  }, [dhtmlxBaselines]);

  // Keep chart rows comfortably tall. Baseline compare adds a dedicated subrow on top of this.
  const GANTT_ROW_HEIGHT = 32;
  const GANTT_BAR_HEIGHT = 20;
  const BASELINE_SUBROW_HEIGHT = 16;
  const BASELINE_BAR_HEIGHT = 4;

  const lookAheadPeriodOptions = useMemo(
    () => lookAheadPeriodChoices(lookAheadConfig),
    [lookAheadConfig],
  );

  useEffect(() => {
    if (lookAheadPeriodOptions.some((choice) => choice.value === lookAheadPeriod)) return;
    const fallback = lookAheadPeriodOptions[0]?.value;
    if (fallback) setLookAheadPeriod(fallback);
  }, [lookAheadPeriod, lookAheadPeriodOptions, setLookAheadPeriod]);

  const targetsWindow = useMemo(
    () => buildTargetsLookAheadWindow(lookAheadPeriod, lookAheadConfig),
    [lookAheadPeriod, lookAheadConfig],
  );

  const lookAheadExportWindowCaption = useMemo(() => {
    if (viewMode !== 'targets') return undefined;
    return formatLookAheadExportWindowCaption(
      targetsWindow.start,
      targetsWindow.end,
      lookAheadPeriod,
      lookAheadConfig,
    );
  }, [viewMode, targetsWindow.start, targetsWindow.end, lookAheadPeriod, lookAheadConfig]);

  /** DPR workingCalender — Day-scale weekend/holiday blocking (portal Daily Targets parity). */
  const { dayOffs: workingDayOffs } = useProjectWorkingCalendar({
    projectId,
    enabled:
      ENABLE_TARGETS_QUANTITY_PANEL &&
      viewMode === 'targets' &&
      lookAheadPeriod === 'day',
  });

  const targetsColumns = useMemo(
    () =>
      lookAheadPeriod === 'day'
        ? applyWorkingCalendarToColumns(targetsWindow.columns, workingDayOffs)
        : targetsWindow.columns,
    [lookAheadPeriod, targetsWindow.columns, workingDayOffs],
  );

  /** Leaves overlapping the look-ahead window — editable targets only for these. */
  const lookAheadLeafTaskIds = useMemo(() => {
    if (viewMode !== 'targets') return null;
    return collectLookAheadLeafTaskIds(
      store.getTasks(),
      targetsWindow.start,
      targetsWindow.end,
    );
  }, [viewMode, store, dhtmlxTasks, targetsWindow.start, targetsWindow.end]);

  /**
   * Look-ahead focus set: those leaves + ancestors for tree context.
   * Used only as an onBeforeTaskDisplay filter — tasks stay in the datastore.
   */
  const targetsFocusTaskIds = useMemo(() => {
    if (viewMode !== 'targets' || targetsShowFullWbs) return null;
    return collectTargetsModeVisibleTaskIds(
      store.getTasks(),
      targetsWindow.start,
      targetsWindow.end,
    );
  }, [
    viewMode,
    targetsShowFullWbs,
    store,
    dhtmlxTasks,
    targetsWindow.start,
    targetsWindow.end,
  ]);

  /** Live DHTMLX critical check used by focus set + strip summary. */
  const liveIsCriticalTask = useMemo(() => {
    void criticalFilterEpoch;
    void dhtmlxTasks;
    const gantt = ganttRef.current?.instance as {
      isTaskExists?: (id: string | number) => boolean;
      getTask?: (id: string | number) => unknown;
      isCriticalTask?: (task: unknown) => boolean;
    } | undefined;
    if (!gantt?.isCriticalTask) return undefined;
    return (t: { id?: string | number }) => {
      try {
        if (!gantt.isTaskExists?.(t.id as string | number)) return false;
        const row = gantt.getTask?.(t.id as string | number);
        return row ? !!gantt.isCriticalTask?.(row) : false;
      } catch {
        return false;
      }
    };
  }, [criticalFilterEpoch, dhtmlxTasks]);

  /** Critical-path focus set (leaves + ancestors). Null when filter is off. */
  const criticalFocusTaskIds = useMemo(() => {
    if (!showCriticalPathOnly) return null;
    return collectCriticalPathVisibleTaskIds({
      allTasks: store.getTaskValues(),
      isCriticalTask: liveIsCriticalTask,
    });
  }, [showCriticalPathOnly, liveIsCriticalTask, store, dhtmlxTasks]);

  /** Strip counts while Critical path focus is on. */
  const criticalPathSummary = useMemo(() => {
    if (!showCriticalPathOnly) return null;
    return summarizeCriticalPath({
      allTasks: store.getTaskValues(),
      isCriticalTask: liveIsCriticalTask,
    });
  }, [showCriticalPathOnly, liveIsCriticalTask, store, dhtmlxTasks]);

  /** Row tint for critical tasks. Independent of the Critical path only filter. */
  useEffect(() => {
    if (!highlightCriticalPath) {
      criticalHighlightPaintRef.current = { ids: new Set(), overdue: false };
    } else {
      const allTasks = store.getTaskValues();
      const summary = summarizeCriticalPath({
        allTasks,
        isCriticalTask: liveIsCriticalTask,
      });
      criticalHighlightPaintRef.current = {
        ids: collectCriticalPathTaskIds({
          allTasks,
          isCriticalTask: liveIsCriticalTask,
        }),
        overdue: summary.overdueCount > 0,
      };
    }
    const gantt = ganttRef.current?.instance as { $container?: HTMLElement } | undefined;
    const ganttRoot =
      gantt?.$container ??
      (document.querySelector('.fbt-gantt-root') as HTMLElement | null);
    if (ganttRoot) {
      syncVisibleCriticalHighlightClasses(ganttRoot, criticalHighlightPaintRef.current);
    }
  }, [highlightCriticalPath, liveIsCriticalTask, store, dhtmlxTasks, criticalFilterEpoch, calendarReady]);

  /** Task-name search focus set (matches + ancestors). Null when query is empty. */
  const taskNameSearchResult = useMemo(() => {
    const q = normalizeTaskSearchQuery(debouncedTaskSearch);
    if (!q) return null;
    return collectTaskNameSearchVisibleTaskIds(store.getTaskValues(), q);
  }, [debouncedTaskSearch, store, dhtmlxTasks]);

  const searchFocusTaskIds = taskNameSearchResult?.visibleIds ?? null;

  const assigneeFilterOptions = useMemo(
    () => listAssigneeFilterOptions(liveAssignments),
    [liveAssignments],
  );

  const columnFilterResult = useMemo(() => {
    if (!hasActiveColumnFilters(debouncedColumnFilterValues)) return null;
    const defs = getFilterableColumnDefs(visibleOptionalColumns);
    const assigneeUserIdsByTaskId = new Map<string, string[]>();
    if (debouncedColumnFilterValues.assignees) {
      for (const t of store.getTaskValues()) {
        const id = String(t.id);
        const locId =
          String(t.locationId ?? '').trim() ||
          String(effectiveLocationIdByTaskId.get(id) ?? '').trim();
        const rows = resolveAssignmentsForTask({
          taskId: id,
          effectiveLocationId: locId || null,
          assignments: liveAssignments,
          locationNameById: locationIdToName,
        });
        const userIds = [
          ...new Set(rows.map((r) => String(r.userId).trim()).filter(Boolean)),
        ];
        assigneeUserIdsByTaskId.set(id, userIds);
      }
    }
    const predecessorsSrNosByTaskId = new Map<string, string[]>();
    if (debouncedColumnFilterValues.predecessors) {
      for (const t of store.getTaskValues()) {
        const id = String(t.id);
        const predLinks = predSuccLinkIndex.byTarget.get(id) ?? [];
        const srNos: string[] = [];
        for (const link of predLinks) {
          const snap = store.getTask(link.source);
          const sr = snap?.srNo != null ? String(snap.srNo).trim() : '';
          if (sr) srNos.push(sr);
        }
        predecessorsSrNosByTaskId.set(id, srNos);
      }
    }
    return collectColumnFilterVisibleTaskIds(
      store.getTaskValues(),
      debouncedColumnFilterValues,
      defs,
      {
        locationNameById: locationIdToName,
        assigneeUserIdsByTaskId,
        predecessorsSrNosByTaskId,
        getLiveMeta: (taskId) => {
          const tracked = trackedLeafTaskIds.has(taskId);
          const snapCritical = !!store.getTask(taskId)?.critical;
          const gantt = ganttRef.current?.instance as {
            isTaskExists?: (id: string) => boolean;
            getTask?: (id: string) => { progress?: number };
            isCriticalTask?: (task: unknown) => boolean;
          } | undefined;
          if (!gantt?.isTaskExists?.(taskId)) {
            return { tracked, critical: snapCritical };
          }
          const task = gantt.getTask?.(taskId);
          let liveCritical = false;
          try {
            liveCritical = task != null ? !!gantt.isCriticalTask?.(task) : false;
          } catch {
            liveCritical = false;
          }
          return {
            progress: task?.progress,
            critical: snapCritical || liveCritical,
            tracked,
          };
        },
      },
    );
  }, [
    debouncedColumnFilterValues,
    visibleOptionalColumns,
    store,
    dhtmlxTasks,
    liveAssignments,
    effectiveLocationIdByTaskId,
    locationIdToName,
    predSuccLinkIndex,
    trackedLeafTaskIds,
  ]);

  const columnFilterFocusTaskIds = columnFilterResult?.visibleIds ?? null;
  const columnFilterMatchCount = columnFilterResult?.matchCount ?? null;

  const activeFilterColumnIds = useMemo(() => {
    if (!hasActiveColumnFilters(debouncedColumnFilterValues)) return new Set<string>();
    return new Set(
      getActiveFilterColumnIds(
        debouncedColumnFilterValues,
        getFilterableColumnDefs(visibleOptionalColumns),
      ),
    );
  }, [debouncedColumnFilterValues, visibleOptionalColumns]);

  const activeFilterLabels = useMemo(() => {
    if (!hasActiveColumnFilters(columnFilterValues)) return [];
    const defs = getFilterableColumnDefs(visibleOptionalColumns);
    const ids = new Set(getActiveFilterColumnIds(columnFilterValues, defs));
    return defs.filter((d) => ids.has(d.id)).map((d) => d.label);
  }, [columnFilterValues, visibleOptionalColumns]);

  /** Display filter for ReactGantt — never strips tasks from `tasks` props. */
  const ganttTaskFilter = useMemo(() => {
    if (
      !targetsFocusTaskIds &&
      !criticalFocusTaskIds &&
      !searchFocusTaskIds &&
      !columnFilterFocusTaskIds
    ) {
      return undefined;
    }
    return (task: { id?: string | number }) => {
      const id = String(task?.id ?? '');
      if (targetsFocusTaskIds && !targetsFocusTaskIds.has(id)) return false;
      if (criticalFocusTaskIds && !criticalFocusTaskIds.has(id)) return false;
      if (searchFocusTaskIds && !searchFocusTaskIds.has(id)) return false;
      if (columnFilterFocusTaskIds && !columnFilterFocusTaskIds.has(id)) return false;
      return true;
    };
  }, [
    targetsFocusTaskIds,
    criticalFocusTaskIds,
    searchFocusTaskIds,
    columnFilterFocusTaskIds,
  ]);

  const {
    assignFilterLeafTasks,
    assignTaskSearchCatalog,
    assignSelectableLeafIds,
    selectVisibleLeavesForAssign,
    deselectVisibleLeavesForAssign,
    openAssignFromBulkSelection,
    assignSelectionTargets,
    assignSelectionTaskIds,
    locationTypeTaskIdsByLocationId,
    handleAssignSelectionTaskIdsChange,
    handleAssignSelectionClearScope,
    handleAssignSelectionLocationIdRemove,
    handleAssignSelectionLocationIdsAdd,
  } = useGanttAssignBulk({
    store,
    parentIdsWithChildren,
    targetsFocusTaskIds,
    criticalFocusTaskIds,
    searchFocusTaskIds,
    columnFilterFocusTaskIds,
    effectiveLocationIdByTaskId,
    dhtmlxTasks,
    assignTaskCatalog,
    selectedAssignTaskIdsRef,
    setSelectedAssignTaskIds,
    calendarReady,
    ganttRef,
    selectedAssignTaskIds,
    assignLocationOptions,
    openAssignUsersDrawer,
    assignMockEnabled,
    assignDrawerOpen,
    assignTargetsLocked,
  });

  const {
    compareBaselinesActive,
    tasks,
    targetsVisibleRows,
    lookAheadSnapshots,
    targetsData,
    ganttTasksForParseRef,
    ganttLinksForParseRef,
    ganttColumns,
    config,
    templates,
    prevPendingHighlightRef,
    handleManualRecalculate,
    leaveBlocker,
    scheduleTasksForReadiness,
    handleReadinessTip,
    readinessSuppressed,
    formatDate,
    applyTaskTypeChange,
    dumpAccumulatedChanges,
    handleBeforeTaskDrag,
    handleAfterTaskDrag,
    handleTaskUpdate,
    openTaskDetailsPanel,
    handleTaskSelected,
    handleTaskDetailsClose,
    handleLinkAdd,
    handleLinkDelete,
    handleBeforeAutoSchedule,
    handleAutoSchedule,
    resolvePredecessorWbsCode,
    handleTaskPanelUpdate,
    handleConvertTaskToWbs,
    handleExportProjectFile,
    handleExportLookAheadExcel,
    handleExportLookAheadPdf,
    handleExportScheduleExcel,
    toolsDataItems,
    toolsExportItems,
    busyOverlay,
    viewSwitchOverlayVisible,
    totalSessionChanges,
    totalTaskCount,
    currentTask,
    currentTaskLocationId,
    currentTaskLocationName,
  } = useGanttEngine({
    pendingEditedFieldsRef,
    effectiveLocationIdByTaskIdRef,
    locationIdToNameRef,
    predecessorPickerTaskIdRef,
    storeLoading,
    loadingProgress,
    projectDisplayName,
    localProcessing,
    viewMode,
    ganttTasksEpoch,
    collapsedBranchIdsRef,
    dhtmlxTasks,
    baselinesByTaskId,
    GANTT_BAR_HEIGHT,
    BASELINE_SUBROW_HEIGHT,
    GANTT_ROW_HEIGHT,
    ganttRef,
    collapsedBranchIds,
    zoomLevel,
    targetsShowFullWbs,
    targetsFocusTaskIds,
    targetsFocusExpandKeyRef,
    targetsWindow,
    setCollapsedBranchIds,
    showCriticalPathOnly,
    criticalFocusTaskIds,
    criticalFocusExpandKeyRef,
    criticalFilterEpoch,
    searchFocusTaskIds,
    searchFocusExpandKeyRef,
    debouncedTaskSearch,
    columnFilterFocusTaskIds,
    columnFilterExpandKeyRef,
    debouncedColumnFilterValues,
    taskNameSearchResult,
    setTaskSearchExpanded,
    taskSearchInputRef,
    calendarReady,
    canTaskCreateRef,
    openAddTaskModalRef,
    openTaskDetailsPanelRef,
    lookAheadLeafTaskIds,
    targetsColumns,
    targetsGateRef,
    links,
    compareBaselinesEnabled,
    compareBaselineIndex,
    dhtmlxBaselines,
    accumulatedChanges,
    visibleOptionalColumns,
    trackedLeafTaskIds,
    parentIdsWithChildren,
    canTaskCreate,
    canTaskDelete,
    canTaskEdit,
    predSuccLinkIndex,
    columnWidthsVersion,
    projectId,
    liveAssignments,
    autoScheduleEnabled,
    showGanttChart,
    BASELINE_BAR_HEIGHT,
    columnResizeInProgressRef,
    columnWidthsRef,
    ganttSkinTheme,
    setViewSwitchBusy,
    locationModalTaskIdRef,
    pendingChangeTaskIdsRef,
    criticalHighlightPaintRef,
    selectedAssignTaskIdsRef,
    escapeHtml,
    locationModalTaskId,
    predecessorPickerTaskId,
    pendingChangeTaskIds,
    pendingEditedFields,
    skipPendingHighlightRefreshRef,
    recalcEngine,
    canMutateTasks,
    scheduleRecalcLoading,
    saving,
    discarding,
    handleServerScheduleRecalculate,
    handleDhtmlxManualRecalculate,
    sessionMetadata,
    readinessDataReady,
    featureTips,
    addTaskModalOpen,
    isPanelOpen,
    locationModalOpen,
    deleteTaskTarget,
    saveConfirmOpen,
    discardConfirmOpen,
    reassignConfirmOpen,
    bulkUnknownTypeConfirmOpen,
    baselineModalOpen,
    liveScheduleOffModalOpen,
    targetsDiscardPending,
    addTaskSubmitting,
    closeAddTaskModal,
    setLocationModalOpen,
    setLocationModalTaskId,
    taskMetaSyncInProgressRef,
    patchGanttTaskFields,
    buildTaskMetaItem,
    getLocationNameById,
    syncTaskMetaInBackground,
    syncInheritedLocationDisplay,
    autoScheduleApplyingRef,
    beginLocalProcessing,
    scheduleBackgroundPersist,
    endLocalProcessing,
    dumpTimer,
    isDragging,
    endDateEditorCommitRef,
    lastLinkPointersRevisionRef,
    linksRevision,
    taskNameInlineEditAllowedRef,
    openLocationModalRef,
    closeLocationInlineEditor,
    predecessorSrNoCodeOverrideRef,
    setColumnWidthsVersion,
    dismissGanttInlineEditing,
    openPredecessorPickerRef,
    closePredecessorPicker,
    setScrollToLinkedIssuesRequest,
    selectedTaskId,
    selectVisibleLeavesForAssign,
    deselectVisibleLeavesForAssign,
    toggleAssignTaskSelected,
    assignLocationOptions,
    assignTaskCatalog,
    openAssignUsersDrawer,
    assignMockEnabled,
    openAddTaskModal,
    setDeleteTaskTarget,
    effectiveLocationIdByTaskId,
    predecessorLinkCommitInProgressRef,
    commitPredecessorLinkChanges,
    autoScheduleMuteSafetyTimerRef,
    highlightCriticalPath,
    setCriticalFilterEpoch,
    resolvePredecessorWbsCodeRef,
    setTrackTaskLoadingTaskId,
    setExporting,
    setExportProgress,
    setExportingExcel,
    reassignLocationsLoading,
    requestReassignLocations,
    bulkUnknownTypeLoading,
    requestBulkUnknownTaskTypes,
    handleInvalidateAndFetch,
    exporting,
    exportingExcel,
    baselineSubmitting,
    lookAheadExportWindowCaption,
    manualRecalculateLoading,
    viewSwitchBusy,
    store,
  });



  // Error state
  if (error || store.getError()) {
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        <h3>Error Loading Zustand Gantt</h3>
        <p>{error || store.getError()}</p>
      </div>
    );
  }


  return (
    <div
      className={[
        'fbt-gantt-root',
        compareBaselinesEnabled ? 'is-baseline-compare' : '',
        ENABLE_TARGETS_QUANTITY_PANEL && viewMode === 'targets'
          ? 'is-targets-mode'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-filter-cols={
        activeFilterColumnIds.size > 0
          ? `|${[...activeFilterColumnIds].join('|')}|`
          : undefined
      }
      style={{
        width: '100%',
        height: 'calc(100vh - 60px)',
        minHeight: '500px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        color: 'var(--mantine-color-text)',
        ...themeVars,
      }}
    >
      <GanttBusyOverlay
        visible={busyOverlay.visible || viewSwitchOverlayVisible}
        title={viewSwitchOverlayVisible ? viewSwitchBusyLabel : busyOverlay.title}
        message={
          viewSwitchOverlayVisible
            ? 'Updating the grid. Large schedules can take a moment.'
            : busyOverlay.message
        }
        mark={viewSwitchOverlayVisible ? viewSwitchMark : busyOverlay.mark}
      />

      {projectId ? (
        <ScheduleReadinessBanner
          projectId={projectId}
          tasks={scheduleTasksForReadiness}
          locationTree={locationTree}
          surface="gantt"
          suppressed={readinessSuppressed}
          dataReady={
            readinessDataReady &&
            calendarReady &&
            !storeLoading &&
            !loadingProgress
          }
          onAddRootTask={() => openAddTaskModal(null)}
          onReassignLocations={requestReassignLocations}
          reassignLocationsLoading={reassignLocationsLoading}
          onVisibleState={(state) => handleReadinessTip(state)}
        />
      ) : null}

      <GanttPageToolbar
        totalTaskCount={totalTaskCount}
        viewMode={viewMode}
        handleViewModeChange={handleViewModeChange}
        lookAheadPeriod={lookAheadPeriod}
        lookAheadPeriodOptions={lookAheadPeriodOptions}
        setLookAheadPeriod={setLookAheadPeriod}
        taskSearchInputRef={taskSearchInputRef}
        taskSearchQuery={taskSearchQuery}
        setTaskSearchQuery={setTaskSearchQuery}
        taskSearchExpanded={taskSearchExpanded}
        setTaskSearchExpanded={setTaskSearchExpanded}
        debouncedTaskSearch={debouncedTaskSearch}
        taskNameSearchResult={taskNameSearchResult}
        handleInvalidateAndFetch={handleInvalidateAndFetch}
        projectId={projectId}
        saving={saving}
        discarding={discarding}
        storeLoading={storeLoading}
        autoScheduleEnabled={autoScheduleEnabled}
        showCriticalPathOnly={showCriticalPathOnly}
        handleAutoScheduleToggle={handleAutoScheduleToggle}
        setShowCriticalPathOnly={setShowCriticalPathOnly}
        manualRecalculateLoading={manualRecalculateLoading}
        scheduleRecalcLoading={scheduleRecalcLoading}
        handleManualRecalculate={handleManualRecalculate}
        recalcEngine={recalcEngine}
        selectedTaskId={selectedTaskId}
        hasUiRootTask={hasUiRootTask}
        openAddTaskModal={openAddTaskModal}
        setRecalcEngine={setRecalcEngine}
        setKeyboardShortcutsOpen={setKeyboardShortcutsOpen}
        toolsExportItems={toolsExportItems}
        allTasksExpanded={allTasksExpanded}
        handleExpandAllToggle={handleExpandAllToggle}
        toolsDataItems={toolsDataItems}
        setCriticalFilterEpoch={setCriticalFilterEpoch}
        highlightCriticalPath={highlightCriticalPath}
        setHighlightCriticalPath={setHighlightCriticalPath}
        compareBaselinesEnabled={compareBaselinesEnabled}
        setCompareBaselinesEnabled={setCompareBaselinesEnabled}
        baselineCompareOptions={baselineCompareOptions}
        compareBaselineIndex={compareBaselineIndex}
        setCompareBaselineIndex={setCompareBaselineIndex}
        baselineVarianceSummary={baselineVarianceSummary}
        compareBaselineLabel={compareBaselineLabel}
        handleOpenSetBaseline={handleOpenSetBaseline}
        baselineSubmitting={baselineSubmitting}
        canSetBaseline={canSetBaseline}
        totalSessionChanges={totalSessionChanges}
        exporting={exporting}
        revisionActionBusy={revisionActionBusy}
        activeRevision={activeRevision}
        baselineInProgress={baselineInProgress}
        canMutateTasks={canMutateTasks}
        targetsShowFullWbs={targetsShowFullWbs}
        setTargetsShowFullWbs={setTargetsShowFullWbs}
        visibleOptionalColumns={visibleOptionalColumns}
        columnFilterValues={columnFilterValues}
        setColumnFilterValues={setColumnFilterValues}
        assigneeFilterOptions={assigneeFilterOptions}
        setVisibleOptionalColumns={setVisibleOptionalColumns}
        setDiscardConfirmOpen={setDiscardConfirmOpen}
        setSaveConfirmOpen={setSaveConfirmOpen}
        handleZoomLevelChange={handleZoomLevelChange}
        zoomLevel={zoomLevel}
        scrollGanttToToday={scrollGanttToToday}
        showGanttChart={showGanttChart}
        setShowGanttChart={setShowGanttChart}
        lookAheadLeafTaskIds={lookAheadLeafTaskIds}
        targetsData={targetsData}
        setTargetsDiscardPending={setTargetsDiscardPending}
        exportProgress={exportProgress}
      />

      {hasActiveColumnFilters(columnFilterValues) ? (
        <div style={{ padding: '0 8px 8px', flexShrink: 0 }}>
          <GanttColumnFilterStrip
            matchCount={columnFilterMatchCount ?? 0}
            activeFilterLabels={activeFilterLabels}
            onClear={() => setColumnFilterValues({})}
          />
        </div>
      ) : null}

      {showCriticalPathOnly && criticalPathSummary ? (
        <div style={{ padding: '0 8px 8px', flexShrink: 0 }}>
          <GanttCriticalPathStrip
            criticalCount={criticalPathSummary.criticalCount}
            overdueCount={criticalPathSummary.overdueCount}
            onTurnOff={() => setShowCriticalPathOnly(false)}
          />
        </div>
      ) : null}

      {selectedAssignTaskIds.size > 0 ? (
        <div style={{ padding: '0 8px 8px', flexShrink: 0 }}>
          <AssignBulkSelectionBar
            selectedCount={selectedAssignTaskIds.size}
            onClear={clearAssignSelection}
            onAssignUsers={openAssignFromBulkSelection}
            assignUsersLabel={
              assignMockEnabled ? 'Assign users (preview)' : 'Assign users'
            }
          />
        </div>
      ) : null}

      {activeRevision ? (
        <div style={{ padding: '0 8px 8px', flexShrink: 0 }}>
          <ActiveBaselineRevisionBanner
            revision={activeRevision}
            busy={revisionActionBusy}
            canAct={canSetBaseline}
            hasUnsavedChanges={totalSessionChanges > 0}
            onSubmit={handleSubmitActiveRevision}
            onDiscard={handleDiscardActiveRevision}
            onApprove={handleApproveActiveRevision}
            onReject={handleRejectActiveRevision}
          />
        </div>
      ) : null}

      <GanttChartPane
        viewMode={viewMode}
        ganttRef={ganttRef}
        ganttSkinTheme={ganttSkinTheme}
        calendarReady={calendarReady}
        tasks={tasks}
        links={links}
        compareBaselinesEnabled={compareBaselinesEnabled}
        dhtmlxBaselines={dhtmlxBaselines}
        config={config}
        plugins={plugins}
        todayMarkers={todayMarkers}
        templates={templates}
        ganttTaskFilter={ganttTaskFilter}
        handleBeforeTaskDrag={handleBeforeTaskDrag}
        handleAfterTaskDrag={handleAfterTaskDrag}
        handleTaskUpdate={handleTaskUpdate}
        handleLinkAdd={handleLinkAdd}
        handleLinkDelete={handleLinkDelete}
        handleBeforeAutoSchedule={handleBeforeAutoSchedule}
        handleAutoSchedule={handleAutoSchedule}
        handleTaskSelected={handleTaskSelected}
        targetsVisibleRows={targetsVisibleRows}
        lookAheadLeafTaskIds={lookAheadLeafTaskIds}
        GANTT_ROW_HEIGHT={GANTT_ROW_HEIGHT}
        targetsColumns={targetsColumns}
        canMutateTasks={canMutateTasks}
        targetsData={targetsData}
        store={store}
      />

      {/* Task details - alternate UIs: right drawer or bottom panel */}
      {taskDetailsDrawerEnabled ? (
        <TaskDetailsDrawer
          store={store}
          onClose={handleTaskDetailsClose}
          onTaskUpdate={handleTaskPanelUpdate}
          onPredecessorUpdate={handleTaskPanelPredecessorUpdate}
          resolvePredecessorWbsCode={resolvePredecessorWbsCode}
          onTrackTask={handleConvertTaskToWbs}
          trackTaskLoadingTaskId={trackTaskLoadingTaskId}
          onSwitchToBottomPanel={() => setTaskDetailsDrawerEnabled(false)}
          compareBaselineIndex={compareBaselinesEnabled ? compareBaselineIndex : null}
          canMutate={canMutateTasks}
          scrollToLinkedIssuesRequest={scrollToLinkedIssuesRequest}
          onPickPredecessors={() => {
            const id = store.getSelectedTaskId();
            if (id) openPredecessorPickerRef.current(String(id), 'panel');
          }}
          onOpenAssignUsers={() => {
            openAssignUsersDrawer({
              prefillSelectedTask: true,
              mode: assignMockEnabled ? 'mock' : 'live',
            });
          }}
          assigneesSummary={selectedTaskAssigneesSummary}
          registerPredecessorDraftMutator={(mutator) => {
            predecessorPanelDraftRef.current = mutator;
          }}
        />
      ) : (
        isPanelOpen && (
          <div
            style={{
              flexShrink: 0,
              height: 420,
              maxHeight: 'min(420px, 45vh)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <TaskPanel
              store={store}
              onClose={handleTaskDetailsClose}
              onTaskUpdate={handleTaskPanelUpdate}
              onPredecessorUpdate={handleTaskPanelPredecessorUpdate}
              resolvePredecessorWbsCode={resolvePredecessorWbsCode}
              onTrackTask={handleConvertTaskToWbs}
              trackTaskLoadingTaskId={trackTaskLoadingTaskId}
              onSwitchToRightPanel={() => setTaskDetailsDrawerEnabled(true)}
              compareBaselineIndex={compareBaselinesEnabled ? compareBaselineIndex : null}
              canMutate={canMutateTasks}
              scrollToLinkedIssuesRequest={scrollToLinkedIssuesRequest}
              onPickPredecessors={() => {
                const id = store.getSelectedTaskId();
                if (id) openPredecessorPickerRef.current(String(id), 'panel');
              }}
              onOpenAssignUsers={() => {
                openAssignUsersDrawer({
                  prefillSelectedTask: true,
                  mode: assignMockEnabled ? 'mock' : 'live',
                });
              }}
              assigneesSummary={selectedTaskAssigneesSummary}
              registerPredecessorDraftMutator={(mutator) => {
                predecessorPanelDraftRef.current = mutator;
              }}
            />
          </div>
        )
      )}

      <GanttPageDialogs
        assignDrawerOpen={assignDrawerOpen}
        setAssignDrawerOpen={setAssignDrawerOpen}
        assignDrawerMode={assignDrawerMode}
        projectId={projectId}
        assignPresentation={assignPresentation}
        assignTargetsLocked={assignTargetsLocked}
        assignSelectionTargets={assignSelectionTargets}
        assignSelectionTaskIds={assignSelectionTaskIds}
        handleAssignSelectionTaskIdsChange={handleAssignSelectionTaskIdsChange}
        handleAssignSelectionLocationIdRemove={handleAssignSelectionLocationIdRemove}
        handleAssignSelectionLocationIdsAdd={handleAssignSelectionLocationIdsAdd}
        handleAssignSelectionClearScope={handleAssignSelectionClearScope}
        assignInitialScope={assignInitialScope}
        assignInitialTaskTargets={assignInitialTaskTargets}
        assignUserOptions={assignUserOptions}
        assignLocationOptions={assignLocationOptions}
        assignTaskSearchCatalog={assignTaskSearchCatalog}
        assignFilterLeafTasks={assignFilterLeafTasks}
        mockAssignments={mockAssignments}
        liveAssignments={liveAssignments}
        setMockAssignments={setMockAssignments}
        setLiveAssignments={setLiveAssignments}
        refreshLiveAssignments={refreshLiveAssignments}
        assignUsersLoading={assignUsersLoading}
        assignAssignmentsLoading={assignAssignmentsLoading}
        locationModalOpen={locationModalOpen}
        closeLocationInlineEditor={closeLocationInlineEditor}
        setLocationModalOpen={setLocationModalOpen}
        setLocationModalTaskId={setLocationModalTaskId}
        locationModalTaskId={locationModalTaskId}
        currentTaskLocationId={currentTaskLocationId}
        currentTaskLocationName={currentTaskLocationName}
        enabledLocationIds={enabledLocationIds}
        assignLocationToTask={assignLocationToTask}
        requestReassignLocations={requestReassignLocations}
        reassignLocationsLoading={reassignLocationsLoading}
        predecessorPickerOpen={predecessorPickerOpen}
        closePredecessorPicker={closePredecessorPicker}
        predecessorPickerTaskId={predecessorPickerTaskId}
        store={store}
        predecessorPickerCurrentInput={predecessorPickerCurrentInput}
        handlePredecessorPickerApplyInput={handlePredecessorPickerApplyInput}
        resolvePredecessorWbsCode={resolvePredecessorWbsCode}
        predecessorPickerMode={predecessorPickerMode}
        predecessorGridShell={predecessorGridShell}
        setPredecessorGridShell={setPredecessorGridShell}
        addTaskModalOpen={addTaskModalOpen}
        addTaskParentId={addTaskParentId}
        addTaskSubmitting={addTaskSubmitting}
        addTaskError={addTaskError}
        closeAddTaskModal={closeAddTaskModal}
        handleCreateTask={handleCreateTask}
        deleteTaskTarget={deleteTaskTarget}
        deleteTaskSubmitting={deleteTaskSubmitting}
        setDeleteTaskTarget={setDeleteTaskTarget}
        handleConfirmDeleteTask={handleConfirmDeleteTask}
        baselineModalOpen={baselineModalOpen}
        baselineModalStep={baselineModalStep}
        projectDisplayName={projectDisplayName}
        baselineSubmitting={baselineSubmitting}
        saving={saving}
        baselineModalBlockedMessage={baselineModalBlockedMessage}
        closeBaselineModal={closeBaselineModal}
        handleBaselineSaveAndContinue={handleBaselineSaveAndContinue}
        handleConfirmCreateBaseline={handleConfirmCreateBaseline}
        saveConfirmOpen={saveConfirmOpen}
        setSaveConfirmOpen={setSaveConfirmOpen}
        sessionMetadata={sessionMetadata}
        handleConfirmSaveToBackend={handleConfirmSaveToBackend}
        reassignConfirmOpen={reassignConfirmOpen}
        setReassignConfirmOpen={setReassignConfirmOpen}
        modalContainer={modalContainer}
        totalSessionChanges={totalSessionChanges}
        confirmReassignLocations={confirmReassignLocations}
        bulkUnknownTypeConfirmOpen={bulkUnknownTypeConfirmOpen}
        bulkUnknownTypeLoading={bulkUnknownTypeLoading}
        setBulkUnknownTypeConfirmOpen={setBulkUnknownTypeConfirmOpen}
        setBulkUnknownTypePreview={setBulkUnknownTypePreview}
        bulkUnknownTypePreview={bulkUnknownTypePreview}
        confirmBulkUnknownTaskTypes={confirmBulkUnknownTaskTypes}
        discardConfirmOpen={discardConfirmOpen}
        setDiscardConfirmOpen={setDiscardConfirmOpen}
        discarding={discarding}
        handleDiscardChanges={handleDiscardChanges}
        targetsDiscardPending={targetsDiscardPending}
        handleTargetsDiscardCancel={handleTargetsDiscardCancel}
        handleTargetsDiscardConfirm={handleTargetsDiscardConfirm}
        liveScheduleOffModalOpen={liveScheduleOffModalOpen}
        setLiveScheduleOffModalOpen={setLiveScheduleOffModalOpen}
        keyboardShortcutsOpen={keyboardShortcutsOpen}
        setKeyboardShortcutsOpen={setKeyboardShortcutsOpen}
        leaveBlocker={leaveBlocker}
        onConfirmLeaveRef={onConfirmLeaveRef}
      />
    </div>
  );
}

/**
 * ZustandGantt - Wrapper with TaskStoreProvider
 */
export default function ZustandGantt() {
  const projectId = getProjectId();
  const onConfirmLeaveRef = useRef<() => Promise<void>>(async () => {});

  if (!projectId) {
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        <h3>Configuration Error</h3>
        <p>No project ID configured. Set VITE_PROJECT_ID in .env</p>
      </div>
    );
  }

  return (
    <TaskStoreProvider projectId={projectId}>
      <ZustandGanttInner onConfirmLeaveRef={onConfirmLeaveRef} />
    </TaskStoreProvider>
  );
}
